import { Css, Palette, Properties, useTestIds } from "@homebound/beam";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "src/components";
import { useToggle } from "src/hooks";
import { HeaderBreadcrumb, PageHeader } from "src/routes/layout/PageHeader";
import { ProjectHeader_ProjectFragment, useProjectHeaderQuery } from "src/routes/projects/ProjectHeader.generated";
import { createProjectDashboardUrl } from "src/RouteUrls";
import { pluralize, queryResult } from "src/utils";

type HierarchyPageHeaderProps = {
  projectId: string;
  title: string;
  createUrl: (projectId: string) => string;
  right?: ReactNode;
  xss?: Properties;
  noBorder?: boolean;
};

/** Same structure as PageHeader, with added support for hierarchy-specific breadcrumbs and project navigation */
export function ProjectHeader(props: HierarchyPageHeaderProps) {
  const { projectId, title, createUrl, right, noBorder = false, xss } = props;
  const query = useProjectHeaderQuery({ variables: { projectId } });
  return queryResult(query, ({ project }) => (
    <PageHeader
      title={title}
      breadcrumb={createHierarchyBreadcrumbs(project, createUrl)}
      collapsibleBreadcrumb
      right={right}
      xss={xss}
      noBorder={noBorder}
    />
  ));
}

export function createHierarchyBreadcrumbs(
  project: ProjectHeader_ProjectFragment,
  createUrl: (projectId: string) => string,
  opts: { excludeProject?: boolean } = {},
): HeaderBreadcrumb[] {
  const parents = [...project.parentsRecursive].reverse();
  const siblings = project.siblingProjects;

  return [
    ...parents.map((parent) => ({ href: createUrl(parent.id), label: parent.name })),
    ...(opts.excludeProject
      ? []
      : [
          {
            href: createUrl(project.id),
            label: project.name,
            right: siblings.nonEmpty && <ProjectsDropdown project={project} createUrl={createUrl} />,
          },
        ]),
  ];
}

type ProjectsDropdownProps = { project: ProjectHeader_ProjectFragment; createUrl: (projectId: string) => string };

function ProjectsDropdown(props: ProjectsDropdownProps) {
  const { project, createUrl } = props;
  const tid = useTestIds({});
  const [expanded, setExpanded] = useToggle(false);
  const [contentHeight, setContentHeight] = useState(expanded ? "auto" : "0");
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const parentProject = project.parentsRecursive.first; // Get the immediate parent project
  const siblingProjects = project.siblingProjects;

  useEffect(() => {
    setContentHeight(expanded && contentRef.current ? `${contentRef.current.scrollHeight}px` : "0");
  }, [expanded]);

  return (
    <div>
      <div css={Css.ml1.hPx(25).onHover.bgGray100.$}>
        <button
          {...tid.toggleDropdown}
          onClick={setExpanded}
          css={{
            ...Css.$,
            transition: "transform 250ms linear",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <Icon icon="chevronDown" />
        </button>
      </div>
      {expanded && (
        <div
          ref={contentRef}
          css={Css.ml1.wPx(300).h(contentHeight).bshBasic.bgWhite.absolute.maxh75.overflowY("auto").z5.$}
        >
          <button
            {...tid.projectsHeader}
            onClick={() => (parentProject ? navigate(createProjectDashboardUrl(parentProject.id)) : undefined)}
            css={Css.w100.df.tal.px2.pyPx(12).if(!!parentProject).onHover.bgBlue50.$}
          >
            {parentProject && (
              <div css={Css.aic.mtPx(1).pr1.mya.$}>
                <Icon {...tid.parentLink} icon="arrowLeft" inc={2.2} />
              </div>
            )}
            All{" "}
            {pluralize(
              siblingProjects.length + 1,
              siblingProjects.first?.__typename ?? "Project",
              siblingProjects.first?.__typename === "Community" ? "Communities" : undefined,
            )}
            {parentProject ? ` in ${parentProject.name}` : ""}
          </button>
          {[project, ...siblingProjects].map((p) => (
            <button
              {...tid.projectLink}
              key={p.id}
              onClick={() => navigate(createUrl(p.id))}
              css={Css.w100.df.fdr.px3.pyPx(12).tal.onHover.bgBlue50.$}
            >
              <div css={Css.w100.$}>{p.name}</div>
              {p.id === project.id && (
                <div css={Css.mya.$}>
                  <Icon icon="check" color={Palette.Blue600} inc={2.4} />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
